import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Search, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Capability, Role } from '@jt/domain';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { OutboundLink } from '../components/LeavingDialog';
import { COMPANY_SITE, COMPANY_SITE_LABEL } from '../lib/site';

/**
 * The help centre.
 *
 * Organised by **what somebody is trying to do**, not by which module the code
 * lives in. Nobody opens help wanting to read about "the contracts module";
 * they open it because a customer has not received a link, or because they
 * cannot work out why a lead will not move to Won.
 *
 * Two rules it follows:
 *
 *  - **Only show what this reader can actually do.** An article about approving
 *    referral rewards is noise to a technician and confusing to a customer,
 *    because neither of them has the button. Each article declares the
 *    capability it needs and the page filters on the live session — the same
 *    list the API enforces.
 *  - **Answer, then explain.** Every article opens with the shortest true
 *    instruction. The reasoning goes underneath for the person who wants it.
 */

type Article = {
  id: string;
  title: string;
  /** Hidden unless the reader holds this. Undefined means everybody. */
  needs?: Capability;
  /** Or restrict to specific roles, where a capability is the wrong axis. */
  roles?: readonly Role[];
  body: string;
  /** Words somebody might type that do not appear in the title. */
  also?: string;
};

type Section = { id: string; title: string; blurb: string; articles: Article[] };

/**
 * The checklist a new person is handed, one per role.
 *
 * Kept as a section rather than a separate page because the question — "what am
 * I supposed to do here?" — is a help question, and a new starter who has been
 * shown one screen is not going to go looking for a second place to look things
 * up. It is first in the list for the same reason.
 *
 * Each is gated on `roles` rather than on a capability. A capability answers
 * "may they press this button"; this answers "is this their job", and those are
 * different questions — a dispatcher holds `stock:edit_list` and does not spend
 * their first week on the stock list.
 */
const SECTIONS: Section[] = [
  {
    id: 'first-week',
    title: 'New here? Start with this',
    blurb: 'A checklist for your role — what you are expected to do, in order.',
    articles: [
      {
        id: 'checklist-technician',
        roles: ['technician'],
        title: 'Technician: your checklist',
        also: 'new starter first day guide what do i do job link finish',
        body: [
          'Most of your work needs no sign-in at all. A link arrives, you tap it.',
          '',
          '**Every day**',
          '',
          '1. Open the job link you were sent. It is for that one job and it is yours —',
          'do not forward it.',
          '',
          '2. **Accept** it, so the office knows it is being worked.',
          '',
          '3. **On my way** when you set off. The customer is told automatically; you do',
          'not need to ring them.',
          '',
          '4. **Arrived** when you are on site.',
          '',
          '5. **Finish**, and fill the report in properly — see below.',
          '',
          '6. Sign in at the end of the day for **Today → My work**: your attendance,',
          'your fuel and expense claims, your leave.',
          '',
          '**Finishing a job properly**',
          '',
          'The form asks for what that job type requires, and it will not let you close',
          'without it. That is deliberate — a job with no report cannot be billed, proved',
          'or learned from.',
          '',
          '- **Photographs**, at least the number asked for.',
          '',
          '- **What you used.** Pick each item and give the quantity. Cable is in metres.',
          'Anything with a serial number needs the serial — that is how a router is traced',
          'if it goes missing.',
          '',
          '- **The customer’s signature**, where the job asks for one.',
          '',
          '**If the thing you fitted is not on the list**, add it: Money & stock → Stock →',
          'Stock list → Add an item. You are allowed to, on purpose. The “other” box is',
          'read by a person and never counted, so anything typed there is invisible to',
          'every figure the business runs on.',
          '',
          '**You cannot raise a ticket**, and that is not a fault. If you find a second',
          'fault while you are on site, add a note to the job you are on and tell',
          'dispatch — they raise it, so it gets an owner and an SLA instead of living in',
          'somebody’s head.',
          '',
          '**Your van can show a negative number.** It means stock reached you without',
          'being booked out. It is the office’s problem, not yours, and it will never stop',
          'you finishing a job.',
        ].join('\n'),
      },
      {
        id: 'checklist-dispatcher',
        roles: ['dispatcher'],
        title: 'Dispatcher: your checklist',
        also: 'new starter first day guide raise a ticket assign board',
        body: [
          'You run the day. The board is your screen.',
          '',
          '**First thing**',
          '',
          '1. Open **Tickets → Board**. Work left to right: anything in the **From**',
          '**the website** column came in overnight from gospeednet.co.ke with nobody',
          'on it.',
          '',
          '2. Then **Unassigned**. Nothing should still be sitting there by mid-morning.',
          '',
          '3. Check what is breaching or close to it. The SLA clock started when the',
          'ticket was raised, not when somebody looked at it.',
          '',
          '**Raising a ticket**',
          '',
          'Tickets → **New ticket**. Find the customer first — searching by phone number',
          'is fastest, and it stops you creating a second record for somebody who is',
          'already there. Then: what is wrong, where, the landmark, and who to ask for on',
          'site. The landmark matters more than the address here.',
          '',
          'Set the priority honestly. Everything marked urgent means nothing is.',
          '',
          '**Assigning it**',
          '',
          'Pick the technician, and the link goes to their phone by itself. You do not',
          'send it. If they have not accepted within a reasonable time, ring them — the',
          'system will not chase for you.',
          '',
          '**Through the day**',
          '',
          '- Keep the board honest. A ticket parked in the wrong column is a lie the',
          'whole business reads.',
          '',
          '- Put a ticket **on hold** when you are genuinely waiting on the customer,',
          'rather than leaving it open and breaching. Say why.',
          '',
          '- If several tickets are one outage, link them to an **incident** so you update',
          'everybody once.',
          '',
          '**What you cannot do**, on purpose: change settings, edit the network tree, or',
          'alter anything a report is measured against. Ask an administrator.',
        ].join('\n'),
      },
      {
        id: 'checklist-sales',
        roles: ['sales'],
        title: 'Sales: your checklist',
        also: 'new starter first day guide lead coverage quote contract',
        body: [
          '**Every lead, in this order**',
          '',
          '1. **Check coverage before you promise anything.** Selling to an address the',
          'fibre does not reach is a refund and a bad review. Coverage is the first',
          'question of every conversation.',
          '',
          '2. Create the **lead** — name, phone, address, what they asked for.',
          '',
          '3. Move it along the funnel as it actually moves. A lead sitting in the wrong',
          'stage is worse than no lead, because the forecast is built on it.',
          '',
          '4. When they say yes: raise the **contract**, check the blanks, and send it.',
          'The customer signs on their phone; you do not chase paper.',
          '',
          '5. Book the installation as a **ticket** so it gets an owner and a date.',
          '',
          '**Things worth knowing early**',
          '',
          '- You can raise a ticket but not assign one. Dispatch decides who goes —',
          'they know who is where.',
          '',
          '- You can send a contract; you cannot amend or cancel a signed one. That is an',
          'administrator, deliberately.',
          '',
          '- You cannot approve referral rewards, including your own.',
          '',
          '- If an address has no coverage, record it as such rather than as a lost lead.',
          'That is what tells the business where to build next.',
          '',
          'You claim fuel and expenses the same way everybody else does: Today → My work.',
        ].join('\n'),
      },
      {
        id: 'checklist-admin',
        roles: ['admin'],
        title: 'Administrator: your checklist',
        also: 'new starter setup first day guide settings users',
        body: [
          '**Setting up, once, in this order**',
          '',
          '1. **Settings** — the company details that appear on every contract and',
          'invoice. Anything left blank prints blank.',
          '',
          '2. **Users** — create everybody with the role that matches their job, not the',
          'role that is easiest. They get a temporary password and must replace it before',
          'they can do anything at all.',
          '',
          '3. **Network** — zones and areas, so tickets can be filed somewhere real.',
          '',
          '4. **Money & stock → Stock → Set up the vans**, then set reorder levels on the',
          'items you actually run out of, then open a **stock count** on the Main store',
          'and enter what is on the shelf. Do not type opening stock as a purchase order.',
          '',
          '5. **Purchasing** — add your suppliers.',
          '',
          '**Every week**',
          '',
          '- Read the **audit log**. It cannot be edited or deleted by anybody, including',
          'you, which is exactly what makes it worth reading.',
          '',
          '- Look at arrears and at what is low on stock.',
          '',
          '- Check nobody is holding a role wider than their job needs.',
          '',
          '**Two things to know before you need them**',
          '',
          '- **A user who has ever done anything cannot be deleted** — deactivate them',
          'instead. Deleting would orphan the audit trail, and the database refuses it.',
          '',
          '- **The last remaining administrator cannot be demoted or deactivated.** Make a',
          'second administrator before you go on leave.',
        ].join('\n'),
      },
      {
        id: 'checklist-accountant',
        roles: ['accountant'],
        title: 'Accountant: your checklist',
        also: 'new starter first day guide billing invoices arrears expenses',
        body: [
          '**Every month**',
          '',
          '1. Run **billing**. It is safe to run twice — a customer already invoiced for',
          'the period is skipped, not billed again.',
          '',
          '2. Work **arrears** oldest first, and record every promise to pay as a note so',
          'the next person is not starting from nothing.',
          '',
          '3. Reconcile M-Pesa. A payment that failed to match is listed rather than',
          'discarded — match it by hand and it lands on the right invoice.',
          '',
          '4. Approve **expense claims**. A claim that needs a receipt cannot be submitted',
          'without one, so anything in front of you is complete.',
          '',
          '**Where the line is**',
          '',
          'You handle money, not people. You can see the roster and approve what somebody',
          'spent; you cannot create an employee or change their contract. That is HR, and',
          'the split is on purpose — the person who can create a person should not also be',
          'the person who can pay them.',
          '',
          'A wrong invoice is credited, not edited. The original stays where it is.',
        ].join('\n'),
      },
      {
        id: 'checklist-hr',
        roles: ['hr'],
        title: 'HR: your checklist',
        also: 'new starter first day guide roster leave attendance contracts',
        body: [
          '**Every week**',
          '',
          '1. Check **attendance** against the roster — who did not clock in, and why.',
          '',
          '2. Work through **leave requests**. An approved day off is what stops dispatch',
          'assigning somebody who is not there.',
          '',
          '3. Keep the roster true. It is what the board plans against.',
          '',
          '**When somebody joins**',
          '',
          '1. Ask an administrator to create their account with the right role.',
          '',
          '2. Raise their **employment contract** and send it. They sign on their phone,',
          'and both sides keep a copy automatically.',
          '',
          '3. Point them at this page — every role has a checklist like this one.',
          '',
          '**Where the line is**',
          '',
          'You handle people, not money. You can see an expense claim and you cannot',
          'approve it — that is the accountant. The split is deliberate: whoever manages',
          'somebody’s employment should not also authorise payments to them.',
          '',
          '**Nobody who has ever acted can be deleted.** When somebody leaves, deactivate',
          'them. Their record stays attached to what they did, which is what makes the',
          'history worth anything.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'start',
    title: 'Getting in',
    blurb: 'Passwords, accounts and who can see what.',
    articles: [
      {
        id: 'first-login',
        title: 'I have been given a password and it will not let me in',
        also: 'temporary password change forced',
        body: `A password somebody else typed is a handover, not a credential — so the account
does nothing until you replace it. Sign in with it, and the only screen you will
reach is the one asking for a new one. Choose it, and everything opens.

This is enforced by the server, not the screen: while a password change is owed,
every other request is refused. That is deliberate. It means a temporary
password handed over on the phone cannot quietly become somebody's permanent
one, and it cannot be used by anybody who overheard it a week later.`,
      },
      {
        id: 'forgot',
        title: 'I have forgotten my password',
        body: `Use "Forgot password" on the sign-in page. A link arrives by email, works once,
and expires in an hour.

The page says the same thing whether or not the address is registered. That is
not unhelpfulness — the addresses here are firstname@gospeednet.co.ke, so a page
that confirmed which ones exist would be a tool for listing who works here.`,
      },
      {
        id: 'roles',
        title: 'What each role can see',
        needs: 'user:manage',
        body: `**Admin** — everything, including settings, users, the audit log and the
contract vault.

**Dispatcher** — runs the day. Tickets, customers, the network as a map to read,
and the problems customers report. Cannot change settings, the network tree, or
anything a report is measured against.

**Technician** — their own jobs, hours, expenses, leave and figures. There is no
request a technician can make that returns a colleague's numbers.

**Sales** — leads, coverage, customers, contracts up to the point of sending
one, and their own funnel. Cannot approve referral rewards, and cannot amend or
cancel a signed contract.

**Customer** — their own package, their own agreements, their own visits, and a
way to report a problem. Nothing else exists for them: they do not hold ticket
access at all, even scoped to themselves.`,
      },
    ],
  },
  {
    id: 'contracts',
    title: 'Contracts',
    blurb: 'Drafting, sending, signing, and what happens when something is wrong.',
    articles: [
      {
        id: 'raise',
        title: 'Write a contract and send it',
        needs: 'contract:draft',
        body: `Open the customer, choose **New contract**, pick their package. The price, speed
and installation fee fill in from the published list, and you can change any of
them — business lines are quoted per site and usually should be.

Press **Sign as the company and send**. That single action does three things: it
applies the branch manager's signature, it freezes the wording, and it emails
the customer a link.

There is deliberately no way to sign as the company without sending, and no way
to send without signing. A customer must never receive a document with an empty
company signature block — that is what a fake one looks like.`,
      },
      {
        id: 'blanks',
        title: 'It says the contract "would go out with blanks in it"',
        needs: 'contract:send',
        body: `Something the template needs is missing from the records — usually the site
address or the account reference.

Use **Preview** to see exactly which. Missing values show as ⟨site.address⟩
rather than as an empty space, precisely so they are obvious before sending
rather than after. Fill the gap in on the customer or site record and send
again.`,
      },
      {
        id: 'amend',
        title: 'A signed contract has a mistake in it',
        needs: 'contract:amend',
        also: 'edit change wrong price typo',
        body: `Use **Raise an amendment**. It creates a new version carrying the old one's
terms, which you correct and send for the customer to sign again. The original
stays in the vault, readable, with its signatures intact.

You cannot edit the signed one, and neither can anybody else — there is no
button, and the API refuses it for every role including admin.

That is not caution for its own sake. Every signature stores a fingerprint of
the exact document it was made against. If the words above a signature could be
changed afterwards, the signature would prove nothing, and a customer could
reasonably say "that is not what I agreed to" with no way to settle it. The
contract page shows that check under **Signatures**; while it says every
signature matches, the document is exactly what was signed.`,
      },
      {
        id: 'link-broken',
        title: 'The customer says the signing link does not work',
        needs: 'contract:send',
        body: `Open the contract and press **Send the link again**. The old link is revoked in
the same action, so they end up with exactly one live link rather than two.

Links last thirty days. Expiry does not affect the agreement itself — it is only
the door, and a new one costs nothing.`,
      },
      {
        id: 'vault',
        title: 'What "locked in the vault" means',
        needs: 'contract:read',
        body: `A signed contract is kept for seven years — what Kenyan tax records are held for
— and cannot be edited or deleted during that time by anyone.

Nothing deletes itself when the seven years pass. The date only decides when a
deletion request would stop being refused outright.`,
      },
    ],
  },
  {
    id: 'sales',
    title: 'Selling',
    blurb: 'Leads, coverage, referrals.',
    articles: [
      {
        id: 'wont-win',
        title: 'It will not let me mark a lead as Won',
        needs: 'lead:write',
        body: `A lead can only become Won from **Contract sent**.

That is the one hard rule in the pipeline, and it exists because "won" is what
the conversion rate is calculated from and what a referral reward is paid
against. If it meant "the salesperson feels good about this one", both numbers
would be fiction.

Everything else is deliberately loose — you can skip from New straight to Quoted
if that is what actually happened.`,
      },
      {
        id: 'coverage',
        title: 'Can we serve this address?',
        needs: 'network:read',
        body: `Open the lead and press **Check the network**. It answers from the network tree,
not from anybody's memory:

**Covered** — there is a live FAT or switchbox in their area.
**Close** — nothing in their area, but live equipment in the same zone. Reachable
with a build.
**Not covered** — neither.

If a surveyor has actually measured the distance, that beats an area boundary
every time; record it and the verdict uses the metres instead.`,
      },
      {
        id: 'no-coverage',
        title: 'Why keep leads we cannot serve?',
        needs: 'lead:read',
        body: `Because a list of the addresses that wanted service and could not get it is a
build plan.

It is the single most valuable by-product a sales team produces, and it is
worthless if those leads are deleted. Mark them **Lost — no coverage**; the
sales report counts them, and the count is the argument for where to lay cable
next.`,
      },
      {
        id: 'referral-approve',
        title: 'A referral says it is not ready to approve',
        needs: 'referral:read',
        body: `The person they referred has to have been connected for thirty days.

Without that wait the programme pays out on customers who cancel in week two,
and the people who work that out keep doing it.

When the thirty days pass, the referral moves to ready and an admin can approve
it. The system never approves one by itself — it does the arithmetic, and a
person decides the money leaves.`,
      },
    ],
  },
  {
    id: 'customers',
    title: 'Customers',
    blurb: 'Their records, and the agreements they sign.',
    articles: [
      {
        id: 'customer-access',
        title: 'Can a customer sign in?',
        needs: 'customer:read',
        also: 'client account portal login',
        body: `No, and there is nothing to set up. Customers hold no account at all.

Everything a customer needs to do with this business, they do from a link we
send them. A contract arrives by email, they open it, read it and sign it there
— no password, no account, nothing to forget. That link is the whole of their
relationship with the system.

There was once a customer portal and it was switched off deliberately. An
account that only ever showed somebody their own contract still needed a
password, a reset flow, a session and an audit trail — a great deal of machinery
guarding a document we were already emailing them. The email does the job with
none of it, and there is no login for anyone to lose.`,
      },
      {
        id: 'no-email',
        title: 'Nothing is sending emails',
        needs: 'settings:write',
        body: `Open **System status**. The panel at the top names the problem and what to change
about it, rather than only reporting that something is off.

The usual answer is that email is still set to "mock", which sends nothing and
is what a fresh install starts with. Settings → Notifications → Email, put in
the credentials, and press **Send test**. That sends a real message; it refuses
to report success against the mock provider, so a pass means a pass.`,
      },
      {
        id: 'links-broken',
        title: 'Technician links open a page that says nothing exists',
        needs: 'system:status',
        body: `Almost always APP_URL still pointing at localhost. Every link the system sends —
job links, signing links, password links — is built from it, so if it is wrong
they are all unopenable from a phone.

System status flags this directly.`,
      },
      {
        id: 'help-from-us',
        title: 'None of this covers my problem',
        body: `Ask in the app: the assistant at the top of every screen answers from your own
data, not from a manual.

Beyond that, the **System status** page shows exactly what is and is not
working, and everything on it is read-only — opening it changes nothing.`,
      },
    ],
  },
  {
    id: 'money',
    title: 'Billing and getting paid',
    blurb: 'Invoices, M-Pesa, and who owes what.',
    articles: [
      {
        id: 'run-billing',
        title: 'Raise this month’s invoices',
        needs: 'billing:issue',
        also: 'monthly billing run invoice generate',
        body: [
          'Money & stock → Billing → Run the billing.',
          '',
          'It raises an invoice for every active subscription that is due, on that',
          'customer’s own billing day. Somebody connected part-way through a month is',
          'charged by the day rather than for the whole of it.',
          '',
          'Press it twice if you are not sure whether it ran. An invoice that already',
          'exists is never raised again — the database refuses a second one for the same',
          'subscription and period, so there is no way to double-bill by pressing a button',
          'again.',
        ].join('\n'),
      },
      {
        id: 'take-payment',
        title: 'Take a payment',
        needs: 'billing:read',
        also: 'mpesa stk push cash bank transfer receipt',
        body: [
          'Two ways, depending on how the money arrives.',
          '',
          '**By M-Pesa.** On the Billing screen, press "Ask for payment" beside the',
          'invoice. The customer’s phone buzzes, they enter their PIN, and the invoice',
          'settles itself. The screen waits and tells you what happened — paid, cancelled',
          'on their phone, no money in the account, or no answer.',
          '',
          '**Everything else.** A bank transfer or cash is recorded by hand. A bank',
          'payment needs its slip reference; cash does not, because demanding one produces',
          'invented references.',
          '',
          'Every payment goes against the oldest unpaid invoice first. Somebody who pays',
          'more than they owe keeps the difference on their account for next month rather',
          'than having it refused.',
        ].join('\n'),
      },
      {
        id: 'mpesa-failed',
        title: 'The M-Pesa prompt did not work',
        needs: 'payment:collect',
        also: 'stk push failed cancelled timeout pin',
        body: [
          'The screen tells you which of these it was, and they need different answers.',
          '',
          '**They cancelled it** — they pressed cancel on the handset. Ask before sending',
          'another; the system will not re-prompt somebody automatically, because that is',
          'harassment.',
          '',
          '**Not enough money** — they need to top up first.',
          '',
          '**No answer** — the phone may be off or out of coverage. The prompt may still',
          'be sitting there, so wait a minute and check the invoice before sending a',
          'second one.',
          '',
          '**Not a Safaricom number** — an Airtel or Telkom line cannot receive an M-Pesa',
          'prompt at all. Ask them to pay to the paybill and record it by hand.',
          '',
          'If it says M-Pesa collection is not set up, an administrator needs to enter the',
          'Daraja credentials under Developer options. The message names exactly which are',
          'missing.',
        ].join('\n'),
      },
      {
        id: 'arrears',
        title: 'Somebody has not paid',
        needs: 'report:arrears',
        also: 'debt overdue suspend grace disconnect chase',
        body: [
          'Money & stock → Arrears.',
          '',
          'The columns matter more than the total. Money owed since last week and money',
          'owed since April are different problems — one is a business trading normally,',
          'the other is money nobody is going to collect.',
          '',
          'An account moves into grace the day after its invoice falls due, and is flagged',
          'for suspension once the grace period runs out. **Nothing is ever suspended',
          'automatically.** The screen tells you an account is past its grace period;',
          'suspending it is done on the subscription, with a reason, and your name goes on',
          'it.',
        ].join('\n'),
      },
      {
        id: 'wrong-invoice',
        title: 'An invoice is wrong',
        needs: 'billing:void',
        also: 'cancel void credit note mistake correct',
        body: [
          'Cancel it with a reason and raise a corrected one. There is no way to edit an',
          'invoice that has been sent, and that is deliberate: a figure a customer paid',
          'against has to stay readable, or an argument about what they owed cannot be',
          'settled.',
          '',
          'Cancelling does not delete anything. The row stays with the reason on it, and',
          'any money that had been applied to it goes back onto the customer’s account to',
          'be used against something else rather than vanishing.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'stock',
    title: 'Stock and buying',
    blurb: 'The store, the vans, and what to order.',
    articles: [
      {
        id: 'stock-setup',
        title: 'Set stock up for the first time',
        needs: 'stock:manage',
        also: 'opening stock count store vans reorder levels',
        body: [
          'Four steps, in this order.',
          '',
          '1. Money & stock → Stock → **Set up the vans**. This creates the Main store and',
          'one van per technician. Safe to press again when you hire somebody.',
          '',
          '2. Scroll to **When to reorder** and set the levels for the items you actually',
          'run out of. Nobody can guess these for you — they depend on how fast you get',
          'through drop fibre and how long your supplier takes. An item left blank is never',
          'reported as low.',
          '',
          '3. **Purchasing** → add your suppliers.',
          '',
          '4. Open a **stock count** on the Main store and enter what is actually on the',
          'shelf. Do not type your opening stock as a purchase order — a count puts every',
          'item in as an adjustment with your name and the date on it, which is the',
          'difference between a figure you can defend and one somebody typed.',
        ].join('\n'),
      },
      {
        id: 'add-stock-item',
        title: 'Add something to the stock list',
        needs: 'stock:edit_list',
        also: 'short code alias also called category counted measured metres serial new item catalogue',
        body: [
          'Money & stock → Stock → **Stock list** → **Add an item**. Every field on that',
          'form has a ⓘ next to it that answers this in place; what follows is the same',
          'thing in one piece.',
          '',
          '**Do it rather than using the “other” box.** Anything typed into the other box',
          'on a job sheet is read by a person and never counted, so it is invisible to',
          'every figure the business runs on. Adding the item takes a minute and anyone',
          'who can see the list can do it — technicians included, on purpose, because the',
          'person who finds out an item is missing is the one standing in front of it.',
          '',
          '**Short code — you do not have to invent one.** Type the name first and the',
          'code fills itself in: “Drop wire clamp” becomes DROP_WIRE_CLAMP. Change it if',
          'you want something shorter. It is what every movement, job sheet and report',
          'stores instead of the name, which is why it cannot be changed afterwards — and',
          'why, when two things are nearly the same, the difference belongs in the code:',
          'CLAMP_DROP and CLAMP_ADSS, not CLAMP and CLAMP2. The name itself can be',
          'corrected any time.',
          '',
          '**Also called** is the nickname — what the crews say on site, or what the',
          'supplier prints on the invoice. It only affects searching: put “P-clamp” here',
          'and looking for a P-clamp finds it. If a word keeps turning up in the other',
          'box, that word belongs in this field.',
          '',
          '**Category** decides how it is grouped on the technician’s checklist and in',
          'every stock report. Pick by what the thing is *for*, not what it is made of: a',
          'steel bracket that holds a cable on a pole is aerial hardware, not consumables.',
          'Each one is explained under the dropdown as you pick it.',
          '',
          '**Counted or measured** decides which question the technician is asked on the',
          'job. Counted is whole things — routers, clamps, closures, poles. Measured is',
          'anything off a drum or a roll and cut to fit — drop wire, ADSS, conduit, draw',
          'rope. You enter no length here; picking measured is what makes their number a',
          'number of metres and takes that many metres off the van. Get it right first',
          'time: it cannot be changed once the item has been counted anywhere, because it',
          'would change what every earlier number meant.',
          '',
          '**Most per visit** is optional and is only a ceiling — it queries an obviously',
          'slipped finger, a 1000 where 100 was meant. On a measured item it is in metres.',
          'Leaving it empty is fine.',
          '',
          '**Needs a serial number** makes the job impossible to finish without typing the',
          'serial off the label, so tick it only for things that carry one: ONTs, routers,',
          'switches, media converters, test meters. That serial is how a device that goes',
          'missing is traced back to the job it left on. Leave it off for anything',
          'identical to the next one out of the box. Rule of thumb: if there is a barcode',
          'with a number under it that differs from the one beside it, tick it. A measured',
          'item cannot have one at all — a length off a drum has no sticker.',
        ].join('\n'),
      },
      {
        id: 'admin-lists',
        title: 'Change a job type, zone, supplier, store or van',
        needs: 'settings:read',
        also: 'lists reference data job type zone supplier van store retire restore archive delete catalogue',
        body: [
          'System → **Lists**. Four lists in one place, all behaving the same way.',
          '',
          '**Job types** — the kinds of work you do. Each carries the priority a new',
          'ticket of that kind starts on and how long one usually takes, so both are worth',
          'setting honestly: the first decides what the SLA clock is set to, the second is',
          'what a technician’s day is planned against.',
          '',
          '**Dispatch zones** — the coarse grouping areas hang off. A zone’s travel time is',
          'what dispatch uses when an area has none of its own.',
          '',
          '**Suppliers** — correct their details here, including the KRA PIN. Without that',
          'on their invoice the input VAT on everything you buy from them cannot be',
          'reclaimed.',
          '',
          '**Stores and vans** — rename them. Worth doing when a van still carries the name',
          'of somebody who left. They are created by “Set up the vans” on the stock screen,',
          'which knows the roster, so there is nothing to add here.',
          '',
          '**Nothing is deleted once something has used it.** A ticket names its job type,',
          'an area names its zone, an order names its supplier. Removing the row would not',
          'tidy the list — it would take away the meaning of every record pointing at it.',
          'So it is retired: off every picker, all the history still readable, and back',
          'whenever you press Restore.',
          '',
          'Something nothing has ever used is removed outright, so a name typed wrongly is',
          'not stuck in the list forever. The screen tells you which of the two happened.',
          '',
          'Two refusals you may meet. A zone still holding areas will not retire — an area',
          'whose zone is gone is one nobody can dispatch against; move or retire its areas',
          'first. A store or van still holding stock will not retire — that would not',
          'remove the stock, it would leave the figures counting things nobody can see.',
          '',
          'Renaming anything here is always safe. Every one of these lists keeps a hidden',
          'key that records point at, so correcting the wording re-files nothing.',
        ].join('\n'),
      },
      {
        id: 'stock-categories',
        title: 'Add, rename or retire a stock category',
        needs: 'stock:edit_list',
        also: 'category group heading archive delete restore move reorder taxonomy',
        body: [
          'Money & stock → Stock → **Stock list** → **Categories**.',
          '',
          'These are the headings the stock list is grouped under, on the technician\u2019s',
          'checklist and in every report. Twelve come with the system; the rest are yours.',
          '',
          '**Adding one.** Give it a name and a line saying what belongs in it. Write that',
          'line — a category nobody can define gets picked by position, and a year later',
          'half the stock is in the wrong group with no way to tell which half. Name it by',
          'what the things are *for* rather than what they are made of: a steel bracket',
          'that holds a cable on a pole is aerial hardware, not consumables.',
          '',
          '**Renaming is safe.** Items store a hidden key rather than the name, so',
          'correcting a category\u2019s wording never re-files anything and never changes what',
          'last month\u2019s report meant. That includes the twelve built-in ones — if your',
          'crews say "Poles & stays", call it that.',
          '',
          '**Reordering** puts them in the order you actually work in. It only affects',
          'what people see.',
          '',
          '**Moving items** empties one category into another in a single action, rather',
          'than re-filing them one at a time. Built-in items cannot be moved — they are',
          'fixed — and the message tells you how many stayed put.',
          '',
          '**Archiving is not deleting, and nothing here deletes.** An archived category',
          'comes off every picker, and every record that ever used it stays readable. You',
          'can restore it from the same screen whenever you like, so archiving the wrong',
          'one costs you nothing. A category still holding items refuses to be archived',
          'and says how many are in the way — move them somewhere first.',
          '',
          '**Who can do what.** Adding, renaming, reordering and moving is dispatchers and',
          'administrators. Archiving and restoring is administrators only, because it',
          'takes a category out of everybody\u2019s picker at once. Technicians can add',
          '*items* but not categories: a technician knows what they fitted, and a category',
          'decides how the whole business reports.',
        ].join('\n'),
      },
      {
        id: 'retire-stock-item',
        title: 'Remove something from the stock list',
        needs: 'stock:edit_list',
        also: 'delete retire typo built-in cannot remove',
        body: [
          'Stock list → **Retire** next to the item. What happens depends on whether it',
          'has ever been used, and the message tells you which:',
          '',
          '**Nothing has used it** — it is deleted outright. Somebody who adds a typo and',
          'notices a minute later should not be stuck looking at it forever.',
          '',
          '**It appears on a job sheet or a movement** — it is retired instead. It stops',
          'being offered to technicians and stays readable on every record that names it.',
          'Deleting it would leave those records pointing at nothing.',
          '',
          'The built-in items cannot be removed or renamed by anybody. Job sheets years',
          'old are keyed on those codes, and renaming one would quietly re-label history.',
          'If you do not use one, ignore it — it costs nothing but a line in a list.',
        ].join('\n'),
      },
      {
        id: 'van-negative',
        title: 'A van is showing a negative number',
        needs: 'stock:read_own',
        also: 'minus below zero van stock wrong',
        body: [
          'It means stock reached that technician without being issued through the system —',
          'somebody handed them a router from the store without booking it out.',
          '',
          'This is information, not a fault, and it is deliberately not hidden. It is also',
          'never allowed to block anybody: a technician finishing a job at the end of a long',
          'day records what they fitted, and if the ledger disagrees that is the office’s',
          'problem to sort out, not theirs.',
          '',
          'Fix it with a stock count on that van.',
        ].join('\n'),
      },
      {
        id: 'delivery',
        title: 'A delivery has arrived',
        needs: 'purchase:receive',
        also: 'goods received partial short delivery book in',
        body: [
          'Purchasing → find the order → **Book in a delivery**.',
          '',
          'The form starts with what is still outstanding, because that is usually what',
          'turned up. Change the numbers if the supplier sent fewer — a partial delivery is',
          'normal and the order simply stays open for the rest.',
          '',
          'Anything with a serial number needs it here. This is the only moment the serial',
          'is knowable, and it is what lets a router be traced later if it goes missing.',
          '',
          'More than was ordered is refused, and the message says by how much. Raise a',
          'second order for the extra so the paperwork matches the shelf.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'design',
    title: 'Network design',
    blurb: 'Whether the light will actually reach.',
    articles: [
      {
        id: 'what-design-is',
        title: 'What the design screen is for',
        needs: 'network:read',
        also: 'packet tracer simulator optical budget db dbm splitter',
        body: [
          'Network → Network design.',
          '',
          'It answers one question: will the light reach this customer? A splitter too many,',
          'three hundred metres more than planned, or a fourth splice on a drop, and the ONT',
          'sees nothing. That failure is invisible on a diagram and obvious in decibels.',
          '',
          'Every endpoint is coloured. Green has headroom. Amber closes by almost nothing —',
          'it will work on the day and fail in two years when somebody adds a splice. Red',
          'does not close at all. Orange means too *much* light, which happens on very short',
          'runs with no splitter and is the failure nobody expects.',
          '',
          'It is not a router simulator. GoSpeedNet does not configure routers for a living,',
          'and the thing that costs this business money is fibre, not routing tables.',
        ].join('\n'),
      },
      {
        id: 'design-figures',
        title: 'Give an element its figures',
        needs: 'network:write',
        also: 'metres distance split ratio splices connectors recalculate',
        body: [
          'Click anything on the canvas and fill in the boxes on the right: the metres of',
          'fibre coming into it, its splitter if it has one, and how many splices and',
          'connectors are on the way in. Press Recalculate.',
          '',
          'Be honest about the distances. Saying a run is 400 m when it is 1,400 m produces',
          'a design that passes on screen and a customer with no service — and these figures',
          'are audited, so whoever typed them can be found.',
          '',
          'Drag elements to arrange them. Anything you have not moved is laid out',
          'automatically by its depth in the tree.',
        ].join('\n'),
      },
      {
        id: 'blast-radius',
        title: 'What a break would take out',
        needs: 'network:read',
        also: 'outage affected customers digger cut span blast radius',
        body: [
          'Click the element and read the "If this failed" panel. It lists everything',
          'downstream and counts how many are customer connections.',
          '',
          'Worth doing before a digger arrives rather than after the phone starts ringing.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'lists',
    title: 'Working with lists',
    blurb: 'Searching, sorting, selecting several at once, and getting a spreadsheet out.',
    articles: [
      {
        id: 'search-everything',
        title: 'Find something without knowing which screen it is on',
        also: 'search find lookup phone number reference slash shortcut',
        body: [
          'The box at the top of every screen.',
          '',
          'It looks across tickets, customers, contracts, meters, sites and people at the',
          'same time, and says which kind each answer is — so half a reference or a phone',
          'number gets you to the record without knowing where it lives.',
          '',
          'Press / from anywhere to jump into it, arrow keys to move, Enter to open.',
          '',
          'It never shows you anything you could not already open. A technician searching',
          'finds their own jobs, not everybody’s, and no customers at all.',
        ].join('\n'),
      },
      {
        id: 'sorting',
        title: 'Sort a list by any column',
        also: 'order arrange oldest newest biggest column heading arrows',
        body: [
          'Click a column heading. Click it again to reverse it.',
          '',
          'Priority sorts by urgency rather than alphabetically, and age by the real time',
          'rather than the text — so "3h" does not end up between "30h" and "4h".',
          '',
          'Rows with nothing in that column sink to the bottom whichever way you sort.',
          'That is deliberate: sorting by "last paid" to find who never has should find',
          'them, not bury them under everybody who has.',
          '',
          'On a list long enough to be paged, sorting arranges the page you are looking at',
          'rather than the whole set behind it. The list says so when that applies.',
        ].join('\n'),
      },
      {
        id: 'select-many',
        title: 'Select several rows and do something to them',
        also: 'checkbox tick multiple bulk batch select all three dots menu',
        body: [
          'Tick the box at the left of any row. A bar appears at the top of the list with',
          'the count and what you can do; the box in the heading takes everything on the',
          'page at once.',
          '',
          'Ticking a row does not open it — the tick and the row are separate.',
          '',
          'The count is of everything you have chosen, not everything you can see. If you',
          'tick rows, then filter or page forward, they stay chosen; the bar says how many',
          'of them are on the page in front of you.',
          '',
          'Export sits in the open because it changes nothing. Anything that does — like',
          'archiving — is under the three dots, and asks before it acts.',
        ].join('\n'),
      },
      {
        id: 'export-csv',
        title: 'Get a list out as a spreadsheet',
        also: 'export download csv excel spreadsheet backup report',
        body: [
          'Tick the rows you want, then Export CSV. It opens in Excel, Google Sheets or',
          'anything else that reads a spreadsheet.',
          '',
          'Telephone numbers come out as text rather than numbers, so +254 7… stays',
          'dialable instead of turning into 2.54712e+11 and a leading zero is not eaten.',
          '',
          'Only the rows you ticked are included. To take everything, tick the heading box',
          'first — and remember that takes the page, so set the page size or filter first',
          'if you want the lot.',
        ].join('\n'),
      },
      {
        id: 'import-csv',
        title: 'Bring a spreadsheet in',
        needs: 'customer:write',
        also: 'import upload csv bulk add spreadsheet',
        body: [
          'Where it is offered, it is under the three dots: Import from CSV.',
          '',
          'The easiest way to get the column headings right is to export a few rows first',
          'and use that file as your template — the headings it writes are the headings it',
          'reads.',
          '',
          'Nothing is written until you have seen what was read and confirmed it.',
        ].join('\n'),
      },
      {
        id: 'archive-tickets',
        title: 'Archive tickets, and find them again',
        needs: 'ticket:archive',
        also: 'archive hide delete remove restore old tickets duplicate',
        body: [
          'One at a time from the ticket itself, or several at once: tick them on the',
          'board, then the three dots → Archive these.',
          '',
          'Archiving is not deleting. The ticket keeps its whole history and its SLA',
          'figures, and can be brought back from the ticket itself.',
          '',
          'To see what is archived: All tickets → Archive, beside the status filter.',
          '',
          'Use it for duplicates, tests and anything raised by mistake. A real job that is',
          'finished should be closed rather than archived — closing is what counts it.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'website',
    title: 'The website',
    blurb: 'What arrives from gospeednet.co.ke, and what to do with it.',
    articles: [
      {
        id: 'website-tickets',
        title: 'Tickets that come from the website',
        also: 'form contact quote coverage register careers feedback newsletter enquiry',
        body: [
          'Anything somebody fills in on gospeednet.co.ke arrives here as a ticket. The',
          'board’s first stage, From the website, holds the ones nobody has picked up yet,',
          'and the line at the top of the board counts them.',
          '',
          'The website keeps no copy of its own any more. If a submission is missed here,',
          'it is missed everywhere — which is why that stage is first.',
          '',
          'Seven kinds arrive, and the ticket says which: a general enquiry, a coverage',
          'question, a new connection request, a job application, a business quote, site',
          'feedback, and a newsletter signup.',
          '',
          'A quote is treated as a sales lead, the same as a new connection request.',
          'Feedback is low priority. A newsletter signup is filed straight to the archive —',
          'it is a record rather than a job, and it is still searchable.',
          '',
          'Nothing from a public form can mark itself critical, whatever it claims.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'paying-phone',
    title: 'The paying phone',
    blurb: 'The Android handset that pays meters and passes tokens on.',
    articles: [
      {
        id: 'what-it-is',
        title: 'What the paying phone is for',
        also: 'payphone handset android app what is the phone for',
        body: [
          'An Android phone, in the office, with the GoSpeedNet app on it and a SIM that',
          'holds M-PESA money. It does two jobs, and they are separate.',
          '',
          '**It pays meters.** Somebody presses Pay on the website, the phone takes the',
          'job, dials the M-PESA menu and fills it in. A person standing with the phone',
          'presses Send and types the PIN \\u2014 the app never knows the PIN and cannot',
          'complete a payment on its own.',
          '',
          '**It passes messages on.** Every M-PESA confirmation and KPLC token that',
          'arrives on that SIM is forwarded to the office. Nothing else can do this: a',
          'token arrives as a text to the phone that paid, and no gateway anywhere can',
          'read it.',
          '',
          'The second job is the one that matters most and the one nobody notices,',
          'because when it stops, every screen still looks right.',
        ].join('\n'),
      },
      {
        id: 'install-the-app',
        title: 'Install the app, or update it',
        needs: 'settings:write',
        also: 'apk download install update sideload version',
        body: [
          'Money & stock \\u2192 Power \\u2192 the paying phone panel. Download the APK there and',
          'open it on the handset.',
          '',
          'Android will warn about installing outside the Play Store. That is expected:',
          'the app reads incoming text messages, which the Play Store only allows for',
          'apps whose whole purpose is messaging, so it is installed directly.',
          '',
          'An update installs over the top and keeps everything \\u2014 the pairing, the saved',
          'addresses, the SIM choice. The phone also checks for updates on its own and',
          'offers them.',
          '',
          '**After any install, check the card at the top of Settings.** Android drops',
          'permissions on update, and the card lists every one the phone needs with a',
          'button beside whichever is missing.',
        ].join('\n'),
      },
      {
        id: 'pair-the-phone',
        title: 'Pair the phone with the office',
        needs: 'settings:write',
        also: 'pairing code link phone to office',
        body: [
          'Money & stock \\u2192 Power \\u2192 Pair a phone. The website shows an eight-character',
          'code; type it into the app under Settings.',
          '',
          'Pairing is what lets a payment raised on the website reach the handset. A',
          'phone that is signed in but not paired still pays meters typed on the phone',
          'itself \\u2014 it just never receives the ones started from a laptop.',
          '',
          'The code is good once and expires. If it is refused, take a fresh one rather',
          'than retyping the old.',
        ].join('\n'),
      },
      {
        id: 'readiness-card',
        title: 'The card at the top of Settings, and what each line means',
        also: 'permissions readiness red warning cannot dial accessibility forward tokens',
        body: [
          'The app checks four things about itself and says so in plain words. Any line',
          'in red is a thing the phone cannot currently do.',
          '',
          '**Allowed to dial the M-PESA menu** \\u2014 without this the phone cannot pay at',
          'all. It is the permission Android is most likely to revoke.',
          '',
          '**Able to fill the M-PESA menu in** \\u2014 the accessibility service. Android',
          'switches this off on update, every time, and it has to be turned back on by',
          'hand in Android\\u2019s own settings. The app cannot do it for you.',
          '',
          '**Sending KPLC tokens on to the office** \\u2014 permission to read incoming',
          'messages. **This is the one that fails quietly.** The phone still pays',
          'perfectly well without it, so nothing looks wrong, and every token it',
          'receives stops on that handset instead of reaching the landlord.',
          '',
          '**Paired with the office** \\u2014 whether payments started on the website arrive.',
          '',
          'Since version 1.15.2 the phone reports these to the office on its own, and an',
          'alert goes out the same day if it says it cannot pass messages on. Before',
          'that, the first anybody knew was a landlord ringing about a token that never',
          'came.',
        ].join('\n'),
      },
      {
        id: 'phone-messages-tab',
        title: 'Find a token that did not reach a landlord',
        needs: 'billing:read',
        also: 'token did not arrive landlord missing messages tab resend',
        body: [
          'On the phone: the Messages tab. On the website: Money & stock \\u2192 Power.',
          '',
          'It lists every message the phone has forwarded, what the office made of each',
          'one, and which went nowhere. A token that was never matched to a purchase, or',
          'that was sent to a number that could not receive it, is findable here and can',
          'be sent again from the phone.',
          '',
          '**Sent is not the same as received.** The office hands a token to the network',
          'and records the time; nothing comes back afterwards to say the network refused',
          'it. Where that is known, the screen says so rather than leaving it reading as',
          'delivered.',
          '',
          'The commonest reason a landlord never gets a token is a wrong telephone number',
          'on the meter. Correct it on the meter and send again \\u2014 the token is still',
          'good and nobody has used it.',
        ].join('\n'),
      },
      {
        id: 'phone-went-quiet',
        title: 'The phone has stopped passing messages on',
        also: 'phone not forwarding stopped relaying no tokens arriving deaf',
        body: [
          'Open the app on that handset and read the card at the top of Settings. The',
          'line about sending tokens to the office will be red, and the button beside it',
          'is the whole fix.',
          '',
          'Messages that arrived while it was off are not recoverable \\u2014 the app only',
          'sees a text as it arrives. Check Money & stock \\u2192 Power for purchases still',
          'waiting on a token, and send those again once the phone is relaying.',
          '',
          'The usual causes, in order: the permission was revoked by an update, battery',
          'optimisation stopped the app running in the background, or somebody turned',
          'Forward tokens off.',
        ].join('\n'),
      },
    ],
  },
  {
    id: 'yourself',
    title: 'Your own account',
    blurb: 'Your photo, your password, your role.',
    articles: [
      {
        id: 'photo',
        title: 'Add your photo',
        also: 'profile picture avatar image upload',
        body: [
          'Your account → Your photo → Choose a photo.',
          '',
          'It appears beside your name wherever you turn up — on tickets, on the roster, on',
          'anything you comment on. Without one your initials are used instead, in a colour',
          'that stays the same for you on every screen.',
          '',
          'A JPEG, PNG or WebP under 2 MB. If yours is refused for being too large, most',
          'phones can save a smaller copy — and cropping it square first is worth doing,',
          'since that is how it will be shown.',
        ].join('\n'),
      },
      {
        id: 'change-role',
        title: 'Change somebody’s role',
        needs: 'user:manage',
        also: 'promote demote dispatcher admin permissions job title',
        body: [
          'Users → the role dropdown on their row.',
          '',
          'That changes their *main* role, which is what decides which records they see at',
          'all. Adding a second job — a technician who also sells — is a different thing and',
          'is done from Groups and roles.',
          '',
          'Two things are refused, both because they would lock everybody out: you cannot',
          'change your own admin role, and the last remaining administrator cannot step',
          'down. Promote somebody else first.',
        ].join('\n'),
      },
    ],
  },
];

export function HelpPage() {
  const { can, user } = useSession();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return SECTIONS.map((section) => ({
      ...section,
      articles: section.articles.filter((article) => {
        // The same rule the API enforces, applied to what is worth reading.
        if (article.needs && !can(article.needs)) return false;
        if (article.roles && (!user || !article.roles.includes(user.role))) return false;
        if (!needle) return true;
        return `${article.title} ${article.body} ${article.also ?? ''}`
          .toLowerCase()
          .includes(needle);
      }),
    })).filter((section) => section.articles.length > 0);
  }, [can, user, query]);

  const total = visible.reduce((sum, section) => sum + section.articles.length, 0);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h2 className="font-heading text-2xl">Help</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Organised by what you are trying to do. Only what your account can actually do is
          shown.
        </p>
      </div>

      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          className="input pl-9"
          placeholder="What are you trying to do?"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {total === 0 && (
        <p className="card text-center text-sm text-muted-foreground">
          Nothing matches "{query}". Try fewer words — or ask the assistant at the top of the
          screen, which answers from your own data.
        </p>
      )}

      {visible.map((section) => (
        <section key={section.id} className="space-y-2">
          <div>
            <h3 className="font-heading text-lg">{section.title}</h3>
            <p className="text-sm text-muted-foreground">{section.blurb}</p>
          </div>

          {section.articles.map((article) => {
            const isOpen = open === article.id || query.trim().length > 2;
            return (
              <div key={article.id} className="card !p-0 overflow-hidden">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
                  onClick={() => setOpen(isOpen && open === article.id ? null : article.id)}
                  aria-expanded={isOpen}
                >
                  <span className="text-sm font-medium">{article.title}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {isOpen && (
                  <div className="border-t border-border px-4 py-3">
                    <Prose text={article.body} />
                  </div>
                )}
              </div>
            );
          })}
        </section>
      ))}

      <section className="card">
        <h3 className="font-heading text-base">Still stuck</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The assistant at the top of every screen answers from your own data.{' '}
          {can('system:status') && (
            <>
              <Link to="/status" className="text-primary-600 hover:underline">
                System status
              </Link>{' '}
              shows what is and is not working, and changes nothing by being opened.{' '}
            </>
          )}
          Our own site is at{' '}
          <OutboundLink href={COMPANY_SITE} className="text-primary-600 underline">
            {COMPANY_SITE_LABEL}
          </OutboundLink>
          .
        </p>
      </section>
    </div>
  );
}

/**
 * The tiny bit of formatting these articles need: paragraphs, and **bold**.
 *
 * Not a markdown library. Three lines of code against a dependency, a bundle
 * and a sanitiser, for text that is written in this file and nowhere else.
 */
function Prose({ text }: { text: string }) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
      {text.split('\n\n').map((paragraph, index) => (
        <p key={index}>
          {paragraph.split(/(\*\*[^*]+\*\*)/).map((part, partIndex) =>
            part.startsWith('**') && part.endsWith('**') ? (
              <strong key={partIndex} className="font-semibold text-foreground">
                {part.slice(2, -2)}
              </strong>
            ) : (
              <span key={partIndex}>{part}</span>
            ),
          )}
        </p>
      ))}
    </div>
  );
}

/* ── What changed ────────────────────────────────────────────────────────── */

export function WhatsNewPage() {
  const releases = useQuery({ queryKey: ['releases'], queryFn: api.releases });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="flex items-center gap-2 font-heading text-2xl">
          <Sparkles className="h-5 w-5 text-primary-500" aria-hidden="true" />
          What's new
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          What changed in the application, newest first.
        </p>
      </div>

      {releases.isPending && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
        </div>
      )}

      {releases.data?.releases.map((release) => (
        <article key={release.version} className="card">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-heading text-lg">{release.title}</h3>
            <span className="text-xs text-muted-foreground">
              v{release.version} · {release.date}
            </span>
          </div>
          <div className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
            {release.body}
          </div>
        </article>
      ))}
    </div>
  );
}
